import { useState, useEffect, useCallback } from 'react';
import { apiClient, ApiClientError } from '../services/api-client';

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: {
    id: string;
    username: string;
    role: 'admin' | 'staff';
  } | null;
}

interface LoginCredentials {
  username: string;
  password: string;
}

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null
  });

  // Check for existing authentication on mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          user: null
        });
        return;
      }

      // Verify token with API
      const response = await apiClient.verifyToken();
      
      setAuthState({
        isAuthenticated: true,
        isLoading: false,
        user: response.user
      });

    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('authToken');
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        user: null
      });
    }
  };

  const login = useCallback(async (credentials: LoginCredentials): Promise<boolean> => {
    try {
      const response = await apiClient.login(credentials.username, credentials.password);
      
      localStorage.setItem('authToken', response.token);
      setAuthState({
        isAuthenticated: true,
        isLoading: false,
        user: response.user
      });

      return true;

    } catch (error) {
      console.error('Login failed:', error);
      if (error instanceof ApiClientError) {
        // Handle specific API errors if needed
        console.error('API Error:', error.code, error.message);
      }
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiClient.logout();
    } catch (error) {
      console.error('Logout API call failed:', error);
      // Continue with logout even if API call fails
    } finally {
      // Always clear local state
      localStorage.removeItem('authToken');
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        user: null
      });
    }
  }, []);

  return {
    ...authState,
    login,
    logout,
    checkAuthStatus
  };
};